import { useState, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Button,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { orderService, type FoodOrder } from '@resort-os/api-client';
import { useQuery } from '@tanstack/react-query';
import { useFormatCurrency, PageSection } from '@resort-os/ui';
import type { PageHeader } from '../types/PageHeader';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const ACCENT = '#2C3E2D';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: FoodOrder;
}

interface CalendarPageProps {
  PageHeader: PageHeader;
}

function DailyRevenueLabel({
  date,
  total,
  formatCurrency,
}: {
  date: Date;
  total: number;
  formatCurrency: (v: number) => string;
}) {
  if (total <= 0) return null;
  return (
    <Typography
      sx={{
        fontSize: '0.55rem',
        color: '#2E7D32',
        fontWeight: 600,

        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        px: 0.5,
      }}
    >
      {formatCurrency(total)}
    </Typography>
  );
}

//
export default function CalendarPage({ PageHeader }: CalendarPageProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [view, setView] = useState<'month' | 'week' | 'day'>(isMobile ? 'day' : 'month');
  const [date, setDate] = useState(new Date());
  const [selectedOrder, setSelectedOrder] = useState<FoodOrder | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const formatCurrency = useFormatCurrency();

  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', 'calendar', monthStart.toISOString(), monthEnd.toISOString()],
    queryFn: () =>
      orderService.list({
        page: 1,
        page_size: 500,
        start_date: monthStart.toISOString(),
        end_date: monthEnd.toISOString(),
      } as any),
  });

  const allOrders: FoodOrder[] = ordersData?.data?.data ?? [];

  const events: CalendarEvent[] = useMemo(() => {
    return allOrders
      .filter(order => order.created_at)
      .map(order => ({
        id: order.id,
        title: `#${order.order_number}`,
        start: new Date(order.created_at),
        end: new Date(order.created_at),
        resource: order,
      }));
  }, [allOrders]);

  const dailyRevenue = useMemo(() => {
    const map: Record<string, number> = {};
    allOrders.forEach(order => {
      if (!order.created_at) return;
      const dayKey = format(new Date(order.created_at), 'yyyy-MM-dd');
      map[dayKey] = (map[dayKey] || 0) + (order.total_amount || 0);
    });
    return map;
  }, [allOrders]);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedOrder(event.resource);
    setDetailOpen(true);
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const paymentStatus = event.resource.payment_status;
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      paid: { bg: 'rgba(76, 175, 80, 0.15)', border: '#4CAF50', text: '#2C3E2D' },
      partially_paid: { bg: 'rgba(123, 31, 162, 0.15)', border: '#7B1FA2', text: '#7B1FA2' },
      pending: { bg: 'rgba(255, 152, 0, 0.15)', border: '#FF9800', text: '#E65100' },
      refunded: { bg: 'rgba(244, 67, 54, 0.12)', border: '#F44336', text: '#C62828' },
      cancelled: { bg: 'rgba(158, 158, 158, 0.15)', border: '#9E9E9E', text: '#616161' },
    };
    const c = colors[paymentStatus] || colors.pending;
    return {
      style: {
        backgroundColor: c.bg,
        borderColor: c.border,
        borderWidth: 2,
        borderStyle: 'solid' as const,
        color: c.text,
        fontSize: '0.7rem',

        fontWeight: 500,
        borderRadius: '2px',
        padding: '2px 4px',
      },
    };
  };

  const CustomEvent = ({ event }: { event: CalendarEvent }) => (
    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
        {event.resource.order_number}
      </Typography>
      <Typography sx={{ fontSize: '0.55rem', opacity: 0.8 }}>
        {formatCurrency(event.resource.total_amount)}
      </Typography>
    </Box>
  );

  const DayCell = ({ children, date: dayDate }: any) => {
    const d = dayDate instanceof Date ? dayDate : new Date(dayDate);
    if (isNaN(d.getTime())) return <Box>{children}</Box>;
    const dayKey = format(d, 'yyyy-MM-dd');
    const revenue = dailyRevenue[dayKey] || 0;
    return (
      <Box>
        {children}
        <DailyRevenueLabel date={d} total={revenue} formatCurrency={formatCurrency} />
      </Box>
    );
  };

  return (
    <Box>
      <PageSection>
        <PageHeader title="Orders Calendar" />
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <>
            <Box sx={{ height: { xs: 400, md: 550 }, mb: 3 }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={v => setView(v as 'month' | 'week' | 'day')}
                date={date}
                onNavigate={d => setDate(d)}
                views={['month', 'week', 'day']}
                style={{ height: '100%' }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleSelectEvent}
                components={{
                  event: CustomEvent,
                  dateCellWrapper: DayCell,
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1.5, md: 3 },
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              {(
                [
                  { label: 'Paid', bg: 'rgba(76, 175, 80, 0.3)', border: '#4CAF50' },
                  { label: 'Partially Paid', bg: 'rgba(123, 31, 162, 0.3)', border: '#7B1FA2' },
                  { label: 'Pending', bg: 'rgba(255, 152, 0, 0.3)', border: '#FF9800' },
                  { label: 'Refunded', bg: 'rgba(244, 67, 54, 0.3)', border: '#F44336' },
                  { label: 'Cancelled', bg: 'rgba(158, 158, 158, 0.3)', border: '#9E9E9E' },
                ] as const
              ).map(({ label, bg, border }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: bg,
                      border: `2px solid ${border}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#6B6560',

                      fontSize: '0.7rem',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </PageSection>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0, border: '1px solid #E2DDD8' } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #E2DDD8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Order Details</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#6B6560' }}>
              {selectedOrder?.order_number}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDetailOpen(false)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pb: 3,
            '&.MuiDialogContent-root': { paddingTop: 2 },
          }}
        >
          {selectedOrder && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 3,
                  p: 2,
                  bgcolor: '#F9F9F9',
                  borderLeft: `3px solid ${selectedOrder.payment_status === 'paid' ? '#2E7D32' : selectedOrder.payment_status === 'partially_paid' ? '#7B1FA2' : selectedOrder.payment_status === 'refunded' ? '#F44336' : '#E65100'}`,
                }}
              >
                <Chip
                  label={(selectedOrder.payment_status || 'pending')
                    .replace('_', ' ')
                    .toUpperCase()}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor:
                      selectedOrder.payment_status === 'paid'
                        ? '#E8F5E9'
                        : selectedOrder.payment_status === 'refunded'
                          ? '#FFEBEE'
                          : '#FFF3E0',
                    color:
                      selectedOrder.payment_status === 'paid'
                        ? '#2E7D32'
                        : selectedOrder.payment_status === 'refunded'
                          ? '#C62828'
                          : '#E65100',
                  }}
                />
                <Chip
                  label={selectedOrder.order_type?.replace('_', ' ')}
                  size="small"
                  sx={{ bgcolor: '#F5F5F5' }}
                />
                {selectedOrder.table && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#6B6560' }}>
                    Table: {selectedOrder.table.table_number}
                  </Typography>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 0.5,
                    }}
                  >
                    Guest
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem' }}>
                    {selectedOrder.guest_name || 'Walk-in'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 0.5,
                    }}
                  >
                    Date
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem' }}>
                    {new Date(selectedOrder.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 0.5,
                    }}
                  >
                    Items
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem' }}>
                    {(selectedOrder.items || []).length} items
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 0.5,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: ACCENT }}>
                    {formatCurrency(selectedOrder.total_amount)}
                  </Typography>
                </Grid>
              </Grid>
              {(selectedOrder.items || []).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#6B6560',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 1.5,
                    }}
                  >
                    Order Items
                  </Typography>
                  <Box sx={{ border: '1px solid #E2DDD8' }}>
                    {selectedOrder.items.map((item, idx) => (
                      <Box
                        key={item.id || idx}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 1,
                          px: 2,
                          borderBottom:
                            idx !== (selectedOrder.items || []).length - 1
                              ? '1px solid #E2DDD8'
                              : 'none',
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {item.product_name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: '#6B6560' }}>
                            {item.quantity}x {formatCurrency(item.unit_price)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {formatCurrency(item.total_price)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E2DDD8' }}>
          <Button
            onClick={() => setDetailOpen(false)}
            sx={{
              fontSize: '0.72rem',
              color: '#6B6560',
              borderRadius: 0,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
